import type { ReactNode } from 'react';
import { mensajesOf, modulesOf, reunionesOf, type Snapshot } from '../lib/snapshot-types.js';
import { porcentaje } from '../lib/etiquetas-metricas.js';
import { Sparkline } from './Sparkline.js';

/**
 * LA FILA RESUMEN DEL PERIODO (revamp del 6-oct): Llamadas, Correos, WhatsApp y
 * Reuniones, cada una con su cifra, la serie de sus dias y una cifra secundaria
 * (conectadas, tasa de respuesta, respondieron y asistencia). Sustituye a la
 * tarjeta fija de "Hoy" y a los indicadores del trimestre: TODO sale del periodo
 * y de las personas elegidas, igual que el resto del panel.
 *
 * Cada tarjeta abre la lista de lo que suma (mismo filtro, mismas filas). Lo que
 * no se puede saber es un guion, nunca un 0.
 */

type Clave = 'llamadas' | 'correos' | 'whatsapp' | 'reuniones';

interface Tarjeta {
  clave: Clave;
  titulo: string;
  valor: number | null;
  serie: number[];
  pie: ReactNode;
  href: string;
  /** Linea extra (el objetivo de reuniones de quien lo tiene). */
  extra?: ReactNode;
}

const numero = (n: number): string => n.toLocaleString('es-ES');
const pct = (v: number | null | undefined): string | null => (v === null || v === undefined || !Number.isFinite(v) ? null : porcentaje(v));

const ICONOS: Record<Clave, ReactNode> = {
  llamadas: <path d="M4.2 2.5h2l1 2.6-1.3.9a7.6 7.6 0 0 0 3.1 3.1l.9-1.3 2.6 1v2a1.4 1.4 0 0 1-1.5 1.4A9.6 9.6 0 0 1 2.8 4a1.4 1.4 0 0 1 1.4-1.5Z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />,
  correos: <><rect x="2" y="3.5" width="12" height="9" rx="1.6" fill="none" stroke="currentColor" strokeWidth="1.3" /><path d="m2.6 4.4 5.4 4.2 5.4-4.2" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" /></>,
  whatsapp: <path d="M8 2.2a5.8 5.8 0 0 0-5 8.7L2.3 13.8l3-.7A5.8 5.8 0 1 0 8 2.2Z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />,
  reuniones: <><rect x="2" y="3" width="12" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.3" /><path d="M2 6.5h12M5.5 1.8v2.6M10.5 1.8v2.6" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></>,
};

/** El pie de correos y WhatsApp, igual en los dos: «16,7 % respondieron», con el «4 de 24 personas» al pasar. */
function Respondieron({ tasa, n, de }: { tasa: number | null | undefined; n: number | null; de: number | null }) {
  const t = pct(tasa);
  const detalle = n !== null && de !== null ? `${numero(n)} de ${numero(de)} personas` : undefined;
  return <span title={detalle}><strong>{t ?? '-'}</strong> respondieron</span>;
}

/**
 * @param objetivoSinMedir hay un objetivo de reuniones en vista que no se puede medir
 *   (su conseguido es desconocido): entonces no se pinta "x de N" contra el objetivo.
 */
export function tarjetasResumen(s: Snapshot, objetivoSinMedir = false): Tarjeta[] {
  const modules = modulesOf(s);
  const t = s.timelines ?? ({} as Snapshot['timelines']);
  const out: Tarjeta[] = [];

  if (modules.calls) {
    const conectadas = s.calls.connected_calls
      ?? (s.calls.por_persona ? s.calls.por_persona.reduce((a, p) => a + p.conectadas, 0) : null);
    out.push({
      clave: 'llamadas', titulo: 'Llamadas', valor: s.calls.total_calls,
      serie: (t.calls ?? []).map((d) => d.total),
      pie: conectadas !== null
        ? <><strong>{numero(conectadas)}</strong> conectadas{pct(s.rates.calls_connect_rate) && <span className="kpi-pie-suave"> · {pct(s.rates.calls_connect_rate)}</span>}</>
        : <>Conexión <strong>{pct(s.rates.calls_connect_rate) ?? '-'}</strong></>,
      href: '#/calls-list/todas',
    });
  }

  const correos = mensajesOf(s, 'emails');
  out.push({
    clave: 'correos', titulo: 'Correos', valor: s.emails.status === 'ok' ? correos.total : null,
    serie: (t.emails ?? []).map((d) => d.sent),
    pie: s.emails.status !== 'ok'
      ? <>Sin datos de correos</>
      : <Respondieron tasa={s.rates.emails_reply_rate} n={correos.respondieron} de={correos.personas} />,
    href: '#/emails-list?scope=team',
  });

  const wa = mensajesOf(s, 'whatsapp');
  out.push({
    clave: 'whatsapp', titulo: 'WhatsApp', valor: s.whatsapp.status === 'ok' ? wa.total : null,
    serie: (t.whatsapp ?? []).map((d) => d.sent),
    pie: s.whatsapp.status !== 'ok'
      ? <>Sin datos de WhatsApp</>
      : <Respondieron tasa={s.rates.whatsapp_reply_rate} n={wa.respondieron} de={wa.personas} />,
    href: '#/whatsapp-list?scope=team',
  });

  const r = reunionesOf(s);
  const hechas = r.cubos.hechas + (r.cubos.propuesta ?? 0);
  // El objetivo de reuniones solo cuando es de este inquilino y se puede medir (el
  // periodo es el trimestre hasta hoy). Un payload congelado sin modulos no lo pinta.
  const objetivo = modules.meeting_targets && s.meetings.objetivo_medible !== false && r.status === 'ok' && !objetivoSinMedir ? s.meetings.target : null;
  out.push({
    clave: 'reuniones', titulo: 'Reuniones', valor: r.status === 'ok' ? r.total : null,
    serie: (t.meetings ?? []).map((d) => d.scheduled ?? ((d.por_hacer ?? 0) + (d.hechas ?? 0) + (d.no_show ?? 0) + (d.propuesta ?? 0))),
    pie: r.status !== 'ok'
      ? <>No se han podido leer las citas</>
      : <><strong>{r.asistencia === null ? '-' : porcentaje(r.asistencia * 100)}</strong> de asistencia</>,
    href: '#/meetings-list/all',
    extra: objetivo !== null && objetivo > 0 ? (
      <div className="kpi-objetivo" title="Reuniones hechas contra el objetivo del trimestre">
        <span className="kpi-objetivo-barra"><i style={{ width: `${Math.min(100, (100 * hechas) / objetivo)}%` }} /></span>
        <span>{hechas} de {objetivo} ({porcentaje((100 * hechas) / objetivo, 0)})</span>
      </div>
    ) : undefined,
  });
  return out;
}

export function ResumenPeriodo({ snapshot, objetivoSinMedir = false }: { snapshot: Snapshot; objetivoSinMedir?: boolean }) {
  const tarjetas = tarjetasResumen(snapshot, objetivoSinMedir);
  return (
    <section className={`kpis kpis--${tarjetas.length}`} aria-label="Resumen del periodo">
      {tarjetas.map((k, i) => (
        <a key={k.clave} className={`kpi kpi--${k.clave}`} href={k.href} style={{ animationDelay: `${i * 60}ms` }}>
          <div className="kpi-cabeza">
            <svg className="kpi-icono" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">{ICONOS[k.clave]}</svg>
            <span className="kpi-titulo">{k.titulo}</span>
          </div>
          <div className="kpi-valor">{k.valor === null ? '-' : numero(k.valor)}</div>
          <div className="kpi-serie">
            {k.serie.length > 1 && <Sparkline values={k.serie} width={160} height={34} color="var(--kpi-c)" fill="var(--kpi-suave)" strokeWidth={1.8} />}
          </div>
          <div className="kpi-pie">{k.pie}</div>
          {k.extra}
        </a>
      ))}
    </section>
  );
}
